import type { CampaignCreationCapabilities } from "../schemas/campaign-creation";
import type {
  SmartCreationAdGroupDraftContentV2,
  SmartCreationDraftContentV2,
} from "../schemas/smart-creation";
import type {
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewEntityRef,
  SmartCreationReviewGraph,
  SmartCreationReviewTarget,
} from "../schemas/smart-creation-review";
import { reviewTargetMissing } from "./errors";
import { adGroupLineage, adGroupRef, campaignLineage, campaignRef, targetRef } from "./lineage";
import type { AuditedValue, ReviewProvenance, ReviewTarget } from "./types";

export function generatedValue<T>(value: T): AuditedValue<T> {
  return { effective: value };
}

export function userValue<T>(_previous: AuditedValue<T>, value: T): AuditedValue<T> {
  return { effective: value };
}

export function reviewTarget(
  adGroup: SmartCreationAdGroupDraftContentV2,
  target: ReviewTarget,
  _capabilities: CampaignCreationCapabilities,
  _provenance: ReviewProvenance | undefined,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
  precise = false,
): SmartCreationReviewTarget {
  const common = {
    ref: targetRef(adGroup, target, previewGeneratorVersion),
    adGroupLineageKey: adGroupLineage(adGroup, previewGeneratorVersion, precise),
    sourceTargetId: target.sourceTargetId,
    strategyKind: target.strategyKind,
    bid: generatedValue(target.bid),
    excluded: generatedValue(false),
    origin: "generated" as const,
  };
  if ("expressionType" in target) {
    return {
      ...common,
      type: "automatic",
      expressionType: target.expressionType,
      excluded: generatedValue(target.included === false),
    };
  }
  if ("keyword" in target) {
    if (adGroup.targeting.type !== "keyword") {
      throw new Error("A keyword Target must belong to keyword targeting");
    }
    return {
      ...common,
      type: "keyword",
      keyword: target.keyword,
      matchType: generatedValue(adGroup.targeting.matchType),
    };
  }
  if ("asin" in target)
    return {
      ...common,
      type: "product",
      asin: target.asin,
      ...(target.matchType === undefined ? {} : { matchType: target.matchType }),
    };
  if ("brandId" in target) {
    return {
      ...common,
      type: "category_brand",
      categoryId: target.categoryId,
      brandId: target.brandId,
    };
  }
  return { ...common, type: "category", categoryId: target.categoryId };
}

export function graphFromDraftContent(
  draftContent: SmartCreationDraftContentV2,
  capabilities: CampaignCreationCapabilities,
  provenance: ReviewProvenance | undefined,
): SmartCreationReviewGraph {
  const campaigns: SmartCreationReviewCampaign[] = [];
  const adGroups: SmartCreationReviewAdGroup[] = [];
  const targets: SmartCreationReviewTarget[] = [];
  for (const campaign of draftContent.campaigns) {
    const groupRefs = campaign.adGroups.map((adGroup) =>
      adGroupRef(
        adGroup,
        draftContent.previewGeneratorVersion,
        draftContent.structure === "precise",
      ),
    );
    const dailyBudget = generatedValue(campaign.dailyBudget);
    campaigns.push({
      ref: campaignRef(
        campaign,
        draftContent.previewGeneratorVersion,
        draftContent.structure === "precise",
      ),
      name: generatedValue(campaign.name),
      budgetAllocation: generatedValue({ basis: "amount" as const, amount: campaign.dailyBudget }),
      dailyBudget,
      biddingStrategy: generatedValue(campaign.biddingStrategy),
      placements: [],
      excluded: generatedValue(false),
      adGroupLineageKeys: groupRefs.map((ref) => ref.lineageKey),
    });
    for (const adGroup of campaign.adGroups) {
      const groupTargets = adGroup.targeting.targets.map((target) =>
        reviewTarget(
          adGroup,
          target,
          capabilities,
          provenance,
          draftContent.previewGeneratorVersion,
          draftContent.structure === "precise",
        ),
      );
      targets.push(...groupTargets);
      adGroups.push({
        ref: adGroupRef(
          adGroup,
          draftContent.previewGeneratorVersion,
          draftContent.structure === "precise",
        ),
        campaignLineageKey: campaignLineage(
          campaign,
          draftContent.previewGeneratorVersion,
          draftContent.structure === "precise",
        ),
        name: generatedValue(adGroup.name),
        defaultBid: generatedValue(adGroup.defaultBid),
        products: adGroup.products,
        targetLineageKeys: groupTargets.map((target) => target.ref.lineageKey),
        negativeLineageKeys: [],
      });
    }
  }
  return { campaigns, adGroups, targets, negatives: [] };
}

export function sameRef(
  left: SmartCreationReviewEntityRef,
  right: SmartCreationReviewEntityRef,
): boolean {
  return (
    left.kind === right.kind &&
    left.itemKey === right.itemKey &&
    left.lineageKey === right.lineageKey
  );
}

export function requireCampaign(
  graph: SmartCreationReviewGraph,
  ref: SmartCreationReviewEntityRef,
) {
  const campaign = graph.campaigns.find((candidate) => sameRef(candidate.ref, ref));
  if (!campaign) throw reviewTargetMissing(ref);
  return campaign;
}

export function requireAdGroup(graph: SmartCreationReviewGraph, ref: SmartCreationReviewEntityRef) {
  const adGroup = graph.adGroups.find((candidate) => sameRef(candidate.ref, ref));
  if (!adGroup) throw reviewTargetMissing(ref);
  return adGroup;
}

export function requireTarget(graph: SmartCreationReviewGraph, ref: SmartCreationReviewEntityRef) {
  const target = graph.targets.find((candidate) => sameRef(candidate.ref, ref));
  if (!target) throw reviewTargetMissing(ref);
  return target;
}

export function requireNegative(
  graph: SmartCreationReviewGraph,
  ref: SmartCreationReviewEntityRef,
) {
  const negative = graph.negatives.find((candidate) => sameRef(candidate.ref, ref));
  if (!negative) throw reviewTargetMissing(ref);
  return negative;
}

export function draftContentAdGroupFor(
  draftContent: SmartCreationDraftContentV2,
  itemKey: string,
): SmartCreationAdGroupDraftContentV2 {
  const adGroup = draftContent.campaigns
    .flatMap((campaign) => campaign.adGroups)
    .find((candidate) => candidate.itemKey === itemKey);
  if (!adGroup) throw new Error("Review Ad Group is absent from its draft content");
  return adGroup;
}
